/**
 * MODULE: domain/pages/frontend/utils/pageCommentThreadUtils.ts
 * PURPOSE: Group flat page comments into root + reply threads (one reply level)
 */

import type { PageComment } from '../../contracts/pages.types';
import { isCommentResolved } from './blockCommentUtils';

export type PageCommentThread = {
  root: PageComment;
  replies: PageComment[];
};

export function isRootComment(comment: PageComment): boolean {
  return !comment.parent_comment_id;
}

export function buildCommentThreads(comments: PageComment[]): PageCommentThread[] {
  const repliesByParent = new Map<string, PageComment[]>();
  const roots: PageComment[] = [];

  for (const comment of comments) {
    if (comment.parent_comment_id) {
      const existing = repliesByParent.get(comment.parent_comment_id) ?? [];
      existing.push(comment);
      repliesByParent.set(comment.parent_comment_id, existing);
      continue;
    }
    roots.push(comment);
  }

  return roots
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .map((root) => ({
      root,
      replies: (repliesByParent.get(root.id) ?? []).sort((a, b) =>
        a.created_at.localeCompare(b.created_at)
      ),
    }));
}

export function countOpenThreads(threads: PageCommentThread[]): number {
  return threads.filter((thread) => !isCommentResolved(thread.root)).length;
}

export function filterThreadsByResolvedVisibility(
  threads: PageCommentThread[],
  showResolved: boolean
): PageCommentThread[] {
  if (showResolved) {
    return threads;
  }
  return threads.filter((thread) => !isCommentResolved(thread.root));
}

export function findThreadContainingComment(
  threads: PageCommentThread[],
  commentId: string
): PageCommentThread | undefined {
  return threads.find(
    (thread) =>
      thread.root.id === commentId || thread.replies.some((reply) => reply.id === commentId)
  );
}

export function findCommentInThreads(
  threads: PageCommentThread[],
  commentId: string
): PageComment | undefined {
  for (const thread of threads) {
    if (thread.root.id === commentId) {
      return thread.root;
    }
    const reply = thread.replies.find((item) => item.id === commentId);
    if (reply) {
      return reply;
    }
  }
  return undefined;
}
